import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferDetails } from '@/features/offers/types';
import {
  SellerReviewEoiCard,
  SellerAcceptEoiForm,
  ProposedSlot,
  SellerRejectEoiForm,
  DeclineFormFields,
} from './seller';
import { useOnUpdateOffer } from '@/features/offers/hooks';
import { useProposeSlots, useOfferSlots } from '@/features/offers/calendar/hooks';
import { useNotifications } from '@/features/notifications';
import { RejectedCard } from './rejected-card';
import { BuyerPendingCard } from './buyer';
import { MeetingSlotsCard } from './meeting-slots-card';
import { LegacySlotsCard } from './legacy-slots-card';

// ─── Types ───────────────────────────────────────────────────────────────────

type SellerAction = 'idle' | 'accepting' | 'rejecting';
type OfferStatus = 'pending' | 'accepted' | 'rejected' | 'internal_rejected';

type EoiActionContainerProps = {
  readonly offer: OfferDetails;
  readonly mode: string | null;
};

// ─── Main Container ──────────────────────────────────────────────────────────

export function EoiActionContainer({ offer, mode }: EoiActionContainerProps) {
  const status = offer.status.name as OfferStatus;
  const isSeller = mode === 'seller';
  const isBuyer = mode === 'buyer';

  if (isSeller) {
    return <SellerFlow offer={offer} status={status} />;
  }

  if (isBuyer) {
    return <BuyerFlow offer={offer} status={status} />;
  }

  return null;
}

// ─── Seller Flow ─────────────────────────────────────────────────────────────

type SellerFlowProps = {
  readonly offer: OfferDetails;
  readonly status: OfferStatus;
};

function SellerFlow({ offer, status }: SellerFlowProps) {
  const { t } = useTranslation(['offers']);
  const { add } = useNotifications();
  const { slots: offerSlots } = useOfferSlots(offer.id);
  const [action, setAction] = useState<SellerAction>('idle');
  const companyName = offer.company?.name ?? '';
  const opportunityId = offer.opportunity?.id ?? '';

  const { updateOfferStatus } = useOnUpdateOffer({
    id: offer.id,
    opportunity: opportunityId,
  });

  const { proposeSlots, isLoading: isProposing } = useProposeSlots();

  const handleSubmitSlots = useCallback(
    async (slots: ProposedSlot[]) => {
      try {
        await proposeSlots({
          offer_id: offer.id,
          slots: slots.map((s) => s.start_time),
        }).unwrap();

        add({
          id: new Date().toString(),
          title: t('Success'),
          description: t('Your availability has been shared with the buyer.'),
        });
      } catch (error) {
        console.error('Error proposing slots:', error);

        add({
          id: new Date().toString(),
          title: t('Error'),
          description: t('Could not share your availability. Please try again.'),
        });
      }
    },
    [offer.id, proposeSlots, add, t]
  );
  
  const handleSubmitDecline = useCallback(
    (data: DeclineFormFields) => {
      updateOfferStatus({
        status: 'rejected',
        feedback_reject: data.feedback_reject,
        internal_notes_reject: data.internal_notes_reject ?? undefined,
      });
    },
    [updateOfferStatus]
  );

  if (status === 'pending') {
    if (action === 'accepting') {
      return (
        <SellerAcceptEoiForm onBack={() => setAction('idle')} onSubmit={handleSubmitSlots} isLoading={isProposing} />
      );
    }

    if (action === 'rejecting') {
      return (
        <SellerRejectEoiForm
          companyName={companyName}
          onBack={() => setAction('idle')}
          onSubmit={handleSubmitDecline}
        />
      );
    }

    return <SellerReviewEoiCard onAccept={() => setAction('accepting')} onDecline={() => setAction('rejecting')} />;
  }

  if (status === 'accepted') {
    // return <MeetingSlotsCard offerId={offer.id} mode="seller" />;
    if (offerSlots.length > 0) {
      return <MeetingSlotsCard offerId={offer.id} mode="seller" />;
    }
    if (offer.availability && offer.availability.length > 0) {
      return <LegacySlotsCard slots={offer.availability} meetingTime={offer.meetingTime} />;
    }
  }

  if (status === 'rejected') {
    return (
      <RejectedCard
        mode="seller"
        companyName={companyName}
        feedbackReject={offer.feedbackReject ?? null}
        ratingComments={offer.rating?.comments ?? null}
        internalNotesReject={offer.internalNotesReject ?? null}
      />
    );
  }

  return null;
}

// ─── Buyer Flow ──────────────────────────────────────────────────────────────

type BuyerFlowProps = {
  readonly offer: OfferDetails;
  readonly status: OfferStatus;
};

function BuyerFlow({ offer, status }: BuyerFlowProps) {
  const { slots: offerSlots } = useOfferSlots(offer.id);

  if (['pending', 'needs_validation'].includes(status)) {
    return <BuyerPendingCard />;
  }

  if (status === 'accepted') {
    // return <MeetingSlotsCard offerId={offer.id} mode="buyer" />;
    if (offerSlots.length > 0) {
      return <MeetingSlotsCard offerId={offer.id} mode="buyer" manualRequestMeeting={offer.manualScheduling} />;
    }
    if (offer.availability && offer.availability.length > 0) {
      return <LegacySlotsCard slots={offer.availability} meetingTime={offer.meetingTime} />;
    }
  }

  if (['rejected', 'internal_rejected'].includes(status)) {
    return (
      <RejectedCard
        mode="buyer"
        companyName={offer.company?.name ?? ''}
        feedbackReject={offer.feedbackReject ?? null}
        ratingComments={offer.rating?.comments ?? null}
        internalNotesReject={offer.internalNotesReject ?? null}
        status={status}
      />
    );
  }

  return null;
}
